import ReportDateRangeFilterBar from '@/components/reports/ReportDateRangeFilterBar'
import {
  AdminHeaderActionButton,
  AdminPageHeader,
  type AdminPageHeaderProps,
} from '@/components/patterns/admin-page'
import { DynamicIcon } from '@/components/ui/DynamicIcon'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { PathLink } from '@/hooks/usePathHelper'
import {
  REPORT_DATE_MODE,
  getCurrentReportDateFilters,
  getOrganizationReportDateBounds,
} from '@/lib/report-date-range'
import { getOrganizationNow } from '@/lib/reporting-dates'
import type { Organization } from '@/lib/services/organizations/organizations.types'
import {
  resolveSalesSummaryGranularityForRange,
  type SalesSummaryReportFilters,
  type SalesSummaryReportSearch,
} from '@/lib/services/reports/reports.schema'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { startTransition, type ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Download, SlidersHorizontal } from 'lucide-react'
import { ALL_FILTER_VALUE } from './sales-statistics/-helpers'

export const SALES_REPORT_SOURCE_FILTER_OPTIONS = [
  { value: 'web', label: 'Website', iconName: 'Globe' },
  { value: 'pos', label: 'POS', iconName: FulfillmentType.InStore.Icon },
]

export const SALES_REPORT_FULFILLMENT_FILTER_OPTIONS = [
  {
    value: FulfillmentType.Delivery.Value,
    label: FulfillmentType.Delivery.Name,
    iconName: FulfillmentType.Delivery.Icon,
  },
  {
    value: FulfillmentType.Pickup.Value,
    label: FulfillmentType.Pickup.Name,
    iconName: FulfillmentType.Pickup.Icon,
  },
  {
    value: FulfillmentType.InStore.Value,
    label: FulfillmentType.InStore.Name,
    iconName: FulfillmentType.InStore.Icon,
  },
]

export function getDefaultSalesReportFilters(organizationTimezone: string | undefined) {
  return getCurrentReportDateFilters(getOrganizationNow(organizationTimezone))
}

/** The filters to navigate to. The selects hand back strings; `validateSearch` checks them. */
type SalesReportFiltersUpdate = Omit<SalesSummaryReportFilters, 'source' | 'fulfillment_type'> & {
  source?: string | null
  fulfillment_type?: string | null
}

type SalesReportToolbarProps = {
  title: string
  description?: ReactNode
  icon: AdminPageHeaderProps['icon']
  routeLink: PathLink
  search: SalesSummaryReportSearch
  filters: SalesSummaryReportFilters
  organization: Organization
  canExport?: boolean
  exportDisabled?: boolean
  exportPending?: boolean
  onExport?: () => void
}

export function SalesReportToolbar({
  title,
  description,
  icon: Icon,
  routeLink,
  search,
  filters,
  organization,
  canExport = false,
  exportDisabled = false,
  exportPending = false,
  onExport,
}: SalesReportToolbarProps) {
  const navigate = useNavigate()
  const organizationNow = getOrganizationNow(organization?.timezone)
  const datePickerBounds = getOrganizationReportDateBounds(
    organization?.created_at,
    organizationNow,
    organization?.timezone
  )

  const applyFilters = ({
    start_date,
    end_date,
    date_mode,
    source,
    fulfillment_type,
    granularity,
  }: SalesReportFiltersUpdate) => {
    const resolvedGranularity = resolveSalesSummaryGranularityForRange(granularity, {
      start_date,
      end_date,
    })

    startTransition(() => {
      void navigate({
        ...routeLink,
        search: {
          start_date,
          end_date,
          date_mode: date_mode ?? undefined,
          source: source ?? undefined,
          fulfillment_type: fulfillment_type ?? undefined,
          granularity: resolvedGranularity,
          search: filters.search ?? undefined,
          sort: filters.sort ?? undefined,
          page_size: filters.page_size ?? undefined,
          page: 1,
        },
        replace: Boolean(
          search.start_date ||
          search.end_date ||
          search.date_mode ||
          search.source ||
          search.fulfillment_type ||
          search.granularity ||
          search.search ||
          search.sort ||
          search.page ||
          search.page_size
        ),
      })
    })
  }

  return (
    <AdminPageHeader
      icon={Icon}
      title={title}
      description={description}
      className="mb-4 mt-2"
      actions={
        <>
          <ReportDateRangeFilterBar
            appliedDateMode={REPORT_DATE_MODE.CUSTOM}
            initialFilters={{
              ...filters,
              ...datePickerBounds,
            }}
            now={organizationNow}
            onApply={({ start_date, end_date, date_mode }) =>
              applyFilters({
                ...filters,
                start_date,
                end_date,
                date_mode,
              })
            }
          />

          <Select
            value={filters.source ?? ALL_FILTER_VALUE}
            onValueChange={(value) =>
              applyFilters({
                ...filters,
                source: value === ALL_FILTER_VALUE ? null : value,
              })
            }
          >
            <SelectTrigger aria-label="Order source" className="w-[200px]">
              <SelectValue placeholder="All order sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_FILTER_VALUE}>
                <SlidersHorizontal className="size-4" />
                All order sources
              </SelectItem>
              {SALES_REPORT_SOURCE_FILTER_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <DynamicIcon iconName={option.iconName} className="size-4" />
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.fulfillment_type ?? ALL_FILTER_VALUE}
            onValueChange={(value) =>
              applyFilters({
                ...filters,
                fulfillment_type: value === ALL_FILTER_VALUE ? null : value,
              })
            }
          >
            <SelectTrigger aria-label="Fulfillment type" className="w-[220px]">
              <SelectValue placeholder="All fulfillment types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_FILTER_VALUE}>
                <SlidersHorizontal className="size-4" />
                All fulfillment types
              </SelectItem>
              {SALES_REPORT_FULFILLMENT_FILTER_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <DynamicIcon iconName={option.iconName} className="size-4" />
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {canExport ? (
            exportDisabled ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>
                    <AdminHeaderActionButton type="button" variant="outline" size="lg" disabled>
                      <Download className="h-4 w-4" />
                      Export Spreadsheet
                    </AdminHeaderActionButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Nothing to export.</TooltipContent>
              </Tooltip>
            ) : (
              <AdminHeaderActionButton
                type="button"
                variant="outline"
                size="lg"
                onClick={onExport}
                disabled={exportPending}
              >
                <Download className="h-4 w-4" />
                Export Spreadsheet
              </AdminHeaderActionButton>
            )
          ) : null}
        </>
      }
    />
  )
}
